import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
  Avatar
} from '@mui/material';
import { closeModal } from '../../../store/slices/uiSlice';
import { clearSelectedInvitation, resendInvitation } from '../../../store/slices/invitationSlice';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString();
};

const getStatusColor = (status) => {
  switch (status) {
    case 'pending': return 'primary';
    case 'used': return 'success';
    case 'expired': return 'error';
    default: return 'default';
  }
};

const InvitationDetailModal = () => {
  const dispatch = useDispatch();
  const { selectedInvitation, loading } = useSelector(state => state.invitations);
  const { modals } = useSelector(state => state.ui);
  const open = modals.invitationDetail;

  const handleClose = () => {
    dispatch(closeModal('invitationDetail'));
    dispatch(clearSelectedInvitation());
  };

  const handleResend = () => {
    dispatch(resendInvitation(selectedInvitation.id));
  };

  if (!selectedInvitation) return null;

  const inv = selectedInvitation;
  const fullName = [inv.first_name, inv.last_name].filter(Boolean).join(' ');

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Invitation Details
        <Chip
          label={inv.status}
          color={getStatusColor(inv.status)}
          size="small"
          sx={{ ml: 2 }}
        />
      </DialogTitle>

      <DialogContent>
        {/* Profile Preview */}
        {fullName && (
          <>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Avatar
                src={inv.profile_image_url || ''}
                sx={{ width: 64, height: 64, bgcolor: '#1976d2' }}
              >
                {inv.first_name?.[0]?.toUpperCase() || '?'}
              </Avatar>
              <Box>
                <Typography variant="h6">{fullName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {inv.specialization || 'No specialization'} &bull; {inv.experience_years || 0} years exp.
                </Typography>
                {inv.bio && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {inv.bio}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Rates */}
            {(inv.price_per_minute_chat || inv.price_per_minute_call || inv.price_per_minute_video) && (
              <Box display="flex" gap={2} mb={2}>
                <Chip label={`Chat: ₹${inv.price_per_minute_chat}/min`} size="small" variant="outlined" />
                <Chip label={`Call: ₹${inv.price_per_minute_call}/min`} size="small" variant="outlined" />
                <Chip label={`Video: ₹${inv.price_per_minute_video}/min`} size="small" variant="outlined" />
              </Box>
            )}
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Invitation Details */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">ID</Typography>
            <Typography variant="body1" gutterBottom>{inv.id}</Typography>

            <Typography variant="subtitle2" color="text.secondary">Email</Typography>
            <Typography variant="body1" gutterBottom>{inv.email}</Typography>

            <Typography variant="subtitle2" color="text.secondary">Status</Typography>
            <Typography variant="body1" gutterBottom>{inv.status}</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">Created At</Typography>
            <Typography variant="body1" gutterBottom>{formatDate(inv.created_at)}</Typography>

            <Typography variant="subtitle2" color="text.secondary">Expires At</Typography>
            <Typography variant="body1" gutterBottom>{formatDate(inv.expires_at)}</Typography>

            <Typography variant="subtitle2" color="text.secondary">Used At</Typography>
            <Typography variant="body1" gutterBottom>{formatDate(inv.used_at)}</Typography>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
            <Typography variant="body1" gutterBottom>
              {inv.notes || 'No notes provided'}
            </Typography>
          </Grid>

          {inv.token && (
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, mt: 1, overflowX: 'auto' }}>
                <Typography variant="subtitle2" color="text.secondary">Invitation Link</Typography>
                <Typography variant="body2" component="div" sx={{ wordBreak: 'break-all' }}>
                  {`${window.location.origin}/verify/${inv.token}`}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        {inv.status === 'pending' && (
          <Button onClick={handleResend} color="primary" disabled={loading}>
            Resend Invitation
          </Button>
        )}
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvitationDetailModal;
